import { REGIONAL_DEFAULTS } from '../contract/regional';

/**
 * Reading a number back out of a string the user typed or the app formatted.
 *
 * `Intl` formats and does not parse, so this is the missing inverse. Everything it needs is still
 * discoverable from `Intl` rather than tabulated: formatting a known value and inspecting the parts
 * reveals the locale's group and decimal separators, and formatting the digits reveals its
 * numbering system. No dictionary, no dependency, and nothing to update when ICU adds a locale.
 *
 * The contract to hold in mind is that this is the inverse of *our own formatting*. Anything the
 * formatter produced for a locale, this reads back in that locale. It is deliberately tolerant of
 * what a person types on top of that — stray spaces, a unit they left on the end — but it does not
 * guess at a locale the caller did not name.
 */

type LocaleNumberParts = {
  readonly group: string;
  readonly decimal: string;
  readonly minusSigns: readonly string[];
  /** Locale digit to ASCII digit. Empty for any locale that already uses Latin digits. */
  readonly digits: Readonly<Record<string, string>>;
};

/** Every dash a user or a locale might use for a negative. */
const DASHES = ['-', '\u2212', '\u2012', '\u2013'];

const LATIN_DIGITS = '1234567890';

/**
 * Locale inspection is not free and a table re-parses the same locale for every row, so the answer
 * is cached. The map is keyed by locale and holds a handful of small objects.
 */
const cache = new Map<string, LocaleNumberParts>();

/**
 * The locale is an argument, not a constant, so the cache is bounded rather than trusted to stay
 * small. This package is a federation singleton and lives as long as the tab does; an unbounded map
 * keyed by caller input is a leak however unlikely the caller. Far above the handful of locales the
 * app supports, so the eviction path is a safety net rather than part of normal operation.
 */
const CACHE_LIMIT = 50;

const inspectLocale = (locale: string): LocaleNumberParts => {
  const format = new Intl.NumberFormat(locale);
  const parts = format.formatToParts(12345.6);

  const partValue = (type: Intl.NumberFormatPartTypes, fallback: string) =>
    parts.find((part) => part.type === type)?.value ?? fallback;

  // Formatting 1234567890 lays the locale's digits out in a known order, which is enough to build
  // the translation table for numbering systems that are not Latin.
  const localeDigits = new Intl.NumberFormat(locale, {
    useGrouping: false,
  }).format(1234567890);

  const digits: Record<string, string> = {};
  for (const [index, digit] of [...localeDigits].entries()) {
    const latin = LATIN_DIGITS[index];
    if (latin && digit !== latin) {
      digits[digit] = latin;
    }
  }

  const localeMinus = new Intl.NumberFormat(locale)
    .formatToParts(-1)
    .find((part) => part.type === 'minusSign')?.value;

  return {
    group: partValue('group', ','),
    decimal: partValue('decimal', '.'),
    minusSigns:
      localeMinus && !DASHES.includes(localeMinus)
        ? [...DASHES, localeMinus]
        : DASHES,
    digits,
  };
};

const numberPartsFor = (locale: string): LocaleNumberParts => {
  const cached = cache.get(locale);
  if (cached) return cached;

  let parts: LocaleNumberParts;
  try {
    parts = inspectLocale(locale);
  } catch {
    // A malformed tag must not throw out of this package, and en-US separators are the closest
    // thing to a neutral guess.
    parts = inspectLocale(REGIONAL_DEFAULTS.locale);
  }

  if (cache.size >= CACHE_LIMIT) cache.clear();

  cache.set(locale, parts);
  return parts;
};

const escapeForClass = (value: string): string =>
  value.replace(/[\\\]^-]/g, '\\$&');

/**
 * The ASCII characters a keyboard produces in place of a typographic separator.
 *
 * Several locales group with a character no keyboard has: `fr-FR` uses a narrow no-break space and
 * `de-CH` a right single quotation mark. Whitespace is already handled generically, so the quote is
 * the only one that needs naming. Without it `"1'234.56"` in `de-CH` reads as one — a wrong answer
 * delivered silently, which is the one outcome this module exists to avoid.
 *
 * The pairing runs both ways because CLDR has moved `de-CH` between the two (ICU 78 groups with the
 * ASCII apostrophe), and text pasted from a document written against the other data still carries
 * the curly one.
 */
const ASCII_LOOKALIKES: Readonly<Record<string, string>> = {
  '\u2019': "'",
  "'": '\u2019',
};

const groupVariants = (group: string): readonly string[] => {
  const lookalike = ASCII_LOOKALIKES[group];
  return lookalike ? [group, lookalike] : [group];
};

const toLatinDigits = (
  text: string,
  digits: Readonly<Record<string, string>>
): string => {
  if (Object.keys(digits).length === 0) return text;

  return [...text].map((character) => digits[character] ?? character).join('');
};

/**
 * Reads a number out of a localized string.
 *
 * Unit suffixes are stripped generically rather than matched against a dictionary of labels: any
 * run of characters that cannot be part of a number is discarded, so `"12.5 km"`, `"12,5 km/h"`
 * and `"12.5"` all read the same. That is dictionary-free, works for a unit this package has never
 * heard of, and leaves label-aware validation to the formatting layer that owns the labels.
 *
 * A label may itself contain digits — `kWh/100km` and `L/100km` are two of this package's own
 * canonical units — so the value is the first number in the string and digits bound into a
 * following word are treated as part of the label. What it will not do is choose between two
 * candidates: a second number standing on its own, two decimal separators, or nothing numeric at
 * all all return `undefined` rather than a guess. `undefined` rather than `NaN`, so a caller cannot
 * let a bad value drift into arithmetic and surface three screens later as a quiet zero.
 *
 * Grouping has to be *well formed*, which is what stops two values being welded into a third. A
 * separator is only a separator where the locale would have put one — between a digit and exactly
 * three more — so `"1 234,56"` reads as one number in `fr-FR` while `"12.5 34"` reads as none.
 * Without that rule, stripping the space turns two numbers into `12.534`, a value neither of them
 * had.
 *
 * The same rule settles the classic ambiguity rather than guessing at it. In `de-DE` a full stop
 * groups thousands, so `"1.5"` is not valid grouping and not a valid decimal either, and the answer
 * is `undefined`. Reading it as fifteen would be defensible and reading it as one and a half would
 * be friendly, but both silently return a value ten times away from what half the users meant.
 * Refusing hands the decision back to the person who knows.
 *
 * @param input - The string to read.
 * @param locale - The locale it is written in. Defaults to `en-US`.
 * @returns The number, or `undefined` if the string does not unambiguously hold one.
 */
export const parseNumber = (
  input: string,
  locale: string = REGIONAL_DEFAULTS.locale
): number | undefined => {
  if (typeof input !== 'string') return undefined;

  const { group, decimal, minusSigns, digits } = numberPartsFor(locale);
  const text = toLatinDigits(input, digits);

  const groupClass = `${groupVariants(group).map(escapeForClass).join('')}\\s`;
  const decimalClass = escapeForClass(decimal);

  // A number is digits, the two separators, and any whitespace a group separator might be made of.
  const numberCharacters = `0-9${groupClass}${decimalClass}`;
  const runPattern = new RegExp(`[${numberCharacters}]+`, 'g');

  const runs = [...text.matchAll(runPattern)].filter(({ 0: run }) =>
    /[0-9]/.test(run)
  );

  const [run] = runs;
  if (!run) return undefined;

  const matched = run[0];
  const trailing = text.slice((run.index ?? 0) + matched.length);

  // Exponent notation would otherwise be read as its mantissa, turning 1e3 into 1. Refusing is the
  // only answer that cannot be off by three orders of magnitude.
  if (/^[eE][+-]?\d/.test(trailing)) return undefined;

  // A marker with nothing usable behind it — `12.5e`, `12.5e+` — is a truncated exponent, and
  // reading it as the mantissa is the same silent error one keystroke earlier. Only when it sits
  // against the digits: the run absorbs the space before a label, so `100 euros` still reads.
  if (!/\s$/.test(matched) && /^[eE][+-]?\s*$/.test(trailing)) return undefined;

  // A separator some other locale uses, sitting against the number with digits behind it. The
  // string was written for a different locale, and reading only the part before it would drop the
  // fraction: `1.5` in fr-FR would come back as one.
  if (/^['.,\u2019\u066b\u066c]\d/.test(trailing)) return undefined;

  // A word carrying digits is a unit; a later number that is not bound into one is a second
  // candidate. Whitespace-separated (`12.5 34`) and punctuation-separated (`12-34`, `12/34`)
  // both count. `kWh/100km` keeps its digits because they sit in a token that also has letters.
  if (
    trailing
      .split(/\s+/)
      .some(
        (token) => token !== '' && /[0-9]/.test(token) && !/\p{L}/u.test(token)
      )
  ) {
    return undefined;
  }

  const preceding = text.slice(0, run.index ?? 0).trimEnd();

  /**
   * A sign is whatever sits against the number, on either side of it — not a property of the whole
   * string. Checking the string's own ends would make `(12.5)` negative and `(12.5) km` positive,
   * a sign flip that turns on whether the user left the unit on.
   */
  const negative =
    minusSigns.some((sign) => preceding.endsWith(sign)) ||
    (preceding.endsWith('(') && trailing.trimStart().startsWith(')'));

  /**
   * Either the digits are grouped the way the locale groups them, or they carry no separator at
   * all. Anything in between — a lone separator with the wrong number of digits after it, or two
   * numbers with a space between them — is not a number this locale would ever have produced.
   *
   * Groups of three, which covers every locale this app ships. Indian numbering groups in twos
   * above the thousand — `1,23,456` — and is refused rather than misread; widening the rule to
   * accept it would also accept mis-grouped input everywhere else, and a refusal is recoverable in
   * a way a wrong number is not.
   */
  const shape = new RegExp(
    `^(?:\\d{1,3}(?:[${groupClass}]\\d{3})+|\\d+)(?:[${decimalClass}]\\d*)?$` +
      `|^[${decimalClass}]\\d+$`
  );

  const body = run[0].trim();
  if (!shape.test(body)) return undefined;

  const withoutGroups = groupVariants(group)
    .reduce((digits, separator) => digits.split(separator).join(''), body)
    .replace(/\s/g, '');

  const value = Number(withoutGroups.split(decimal).join('.'));
  if (!Number.isFinite(value)) return undefined;

  return negative && value !== 0 ? -value : value;
};

/**
 * Testing seam. The cache is a pure memo, so clearing it can only cost time.
 *
 * Named for what it holds rather than "locale cache", because C4 adds a second cache of `Intl`
 * instances and two reset functions that sound alike is a mistake waiting to be made.
 */
export const resetNumberParsingCache = (): void => {
  cache.clear();
};
